import {Action} from 'redux'
import {PayloadAction} from 'util/redux'

/*
 * cleaned up version of createReducer from redux-toolkit without bloat and only with typed content.
 * No default case, no matcher, only typesafe builder callback
 */

export type Reducer<S = any, A extends Action = PayloadAction<any>> = (
	state: S | undefined,
	action: A,
) => S

export type NoInfer<T> = [T][T extends any ? 0 : never]

export interface TypedActionCreator<Type extends string> {
	(...args: any[]): PayloadAction<any, Type>

	type: Type
}

export interface ActionReducerMapBuilder<State> {
	addCase<ActionCreator extends TypedActionCreator<string>>(
		actionCreator: ActionCreator,
		reducer: CaseReducer<State, ReturnType<ActionCreator>>,
	): ActionReducerMapBuilder<State>

	addMatcher<A extends PayloadAction<any>>(
		matcher: ActionMatcher<A>,
		reducer: CaseReducer<State, A>,
	): Omit<ActionReducerMapBuilder<State>, 'addCase'>
}

export function executeReducerBuilderCallback<S>(
	builderCallback: (builder: ActionReducerMapBuilder<S>) => void,
): [CaseReducers<S, any>, ActionMatcherDescriptionCollection<S>] {
	const actionsMap: CaseReducers<S, any> = {}
	const actionMatchers: ActionMatcherDescriptionCollection<S> = []
	const builder = {
		addCase(
			typedActionCreator: TypedActionCreator<any>,
			reducer: CaseReducer<S>,
		) {
			const type = typedActionCreator.type
			if (type in actionsMap) {
				throw new Error(
					'addCase cannot be called with two reducers for the same action type',
				)
			}
			actionsMap[type] = reducer
			return builder
		},
		addMatcher<A extends PayloadAction<any>>(
			matcher: ActionMatcher<A>,
			reducer: CaseReducer<S, A>,
		) {
			actionMatchers.push({matcher, reducer})
			return builder
		},
	} as ActionReducerMapBuilder<S>
	builderCallback(builder)
	return [actionsMap, actionMatchers]
}

export type Actions<T extends keyof any = string> = Record<T, PayloadAction<any>>

interface ActionMatcher<A extends PayloadAction<any>> {
	(action: PayloadAction<unknown>): action is A
}

export type ActionMatcherDescription<S, A extends PayloadAction<any>> = {
	matcher: ActionMatcher<A>
	reducer: CaseReducer<S, NoInfer<A>>
}

export type ActionMatcherDescriptionCollection<S> = Array<ActionMatcherDescription<S, any>>

export type CaseReducer<S = any, A extends PayloadAction<any> = PayloadAction<any>> = (
	state: S,
	action: A,
) => S

export type CaseReducers<S, AS extends Actions> = {
	[T in keyof AS]: AS[T] extends PayloadAction<any> ? CaseReducer<S, AS[T]> : void
}

export function createReducer<S>(
	initialState: S,
	builderCallback: ((builder: ActionReducerMapBuilder<S>) => void),
): Reducer<S> {
	let [actionsMap, finalActionMatchers] = executeReducerBuilderCallback(builderCallback)

	return function (state = initialState, action): S {
		let caseReducers = [
			actionsMap[action.type],
			...finalActionMatchers
				.filter(({matcher}) => matcher(action))
				.map(({reducer}) => reducer),
		]
		return caseReducers.reduce((previousState, caseReducer): S => {
			if (caseReducer) {
				return caseReducer(previousState, action)
			}
			return previousState
		}, state)
	}
}
